import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence, PanInfo } from 'framer-motion';
import { DeckCardData, CardDeckProps } from './types';
import DeckCard from './DeckCard';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import {
  CARD_DECK_DIMENSIONS,
  CARD_DECK_SWIPE,
  CARD_DECK_COLORS,
} from './constants';

const cardStylePositions = [
  {
    scale: CARD_DECK_DIMENSIONS.frontCard.scale,
    y: 0,
    zIndex: 3,
    overlay: 'transparent',
  },
  {
    scale: CARD_DECK_DIMENSIONS.middleCard.scale,
    y: CARD_DECK_DIMENSIONS.middleCard.offsetY,
    zIndex: 2,
    overlay: CARD_DECK_COLORS.middleCardOverlay,
  },
  {
    scale: CARD_DECK_DIMENSIONS.backCard.scale,
    y: CARD_DECK_DIMENSIONS.backCard.offsetY,
    zIndex: 1,
    overlay: CARD_DECK_COLORS.backCardOverlay,
  },
];

// Calculate card styles based on position in stack
const getCardStyle = (index: number) => {
  return cardStylePositions[index] || cardStylePositions[2];
};


/**
 * CardDeck Component
 *
 * Displays a stack of 3 cards with swipe gesture support.
 *
 * Features:
 * - 3 visible cards with depth effect (scale + overlay)
 * - Touch + mouse drag on top card
 * - Swipe threshold triggers card exit animation
 * - Swiped cards cycle to end of deck (infinite)
 * - Sub-threshold swipe snaps back to center
 * - `swipeEnabled` prop to disable drag (e.g. during loading)
 *
 * Animation specs:
 * - Threshold: 100px horizontal drag
 * - Exit: 500px travel with fade out
 * - Snap back: 0.3s spring
 * - Card promotion: Spring animation (stiffness: 260, damping: 20)
 */
export default function CardDeck({ cards: initialCards, onSwipe, swipeEnabled = true }: CardDeckProps) {
  const { track } = useAnalytics();

  // Maintain ordered card array for cycling
  const [cards, setCards] = useState<DeckCardData[]>(initialCards);

  // Sync internal state when parent provides new cards (e.g. React Query placeholder → real data)
  useEffect(() => {
    setCards(initialCards);
  }, [initialCards]);

  // Track which card is currently being swiped (for exit animation)
  const [exitingCard, setExitingCard] = useState<{
    card: DeckCardData;
    direction: 'left' | 'right';
  } | null>(null);

  // Ref to track timeout for cleanup
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track pointer start position to distinguish clicks from drags
  const pointerStart = useRef({ x: 0, y: 0 });

  // Motion values for drag interaction
  const x = useMotionValue(0);

  // Opacity decreases as card moves away from center
  const opacity = useTransform(
    x,
    [-CARD_DECK_SWIPE.exitDistance, 0, CARD_DECK_SWIPE.exitDistance],
    [0, 1, 0]
  );

  // Slight rotation during drag for natural feel
  const rotate = useTransform(x, [-200, 0, 200], [-15, 0, 15]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle drag end - determine if threshold crossed
  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const swipeThreshold = CARD_DECK_SWIPE.threshold;
      const swipeDirection = info.offset.x > 0 ? 'right' : 'left';

      if (Math.abs(info.offset.x) > swipeThreshold) {
        // Threshold crossed - trigger exit animation
        const topCard = cards[0];
        setExitingCard({ card: topCard, direction: swipeDirection });

        if (topCard.type !== 'intro' && topCard.type !== 'journey') {
          track('card_swipe', {
            activity_id: String(topCard.id),
            swipe_direction: swipeDirection,
            card_type: topCard.type || 'activity',
          });
        }

        // For regular cards: fire immediately (unchanged behaviour).
        // For intro card: deferred to inside the timeout so dismissIntro()
        // is never called while the exit animation is still running.
        if (topCard.type !== 'intro') {
          onSwipe?.(topCard.id, swipeDirection);
        }

        // Clear any existing timeout before setting new one
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // After exit animation, remove intro card or cycle regular cards
        timeoutRef.current = setTimeout(() => {
          x.set(0);
          setCards((prevCards) => {
            const [first, ...rest] = prevCards;
            // Use first.type (current head of prevCards), not topCard.type (stale closure)
            if (first.type === 'intro') return rest;
            return [...rest, first];
          });
          setExitingCard(null);
          // Intro card: notify parent only after animation and state mutation are done
          if (topCard.type === 'intro') {
            onSwipe?.(topCard.id, swipeDirection);
          }
          timeoutRef.current = null;
        }, CARD_DECK_SWIPE.exitDuration * 1000);
      } else {
        // Threshold not crossed - ensure x is reset for opacity consistency
        x.set(0);
      }
    },
    [cards, onSwipe, x]
  );

  // Get visible cards (top 3)
  const visibleCards = useMemo(() => cards.slice(0, 3).reverse(), [cards]);

  return (
    <div
      className="relative flex justify-center w-full max-w-[382px] sm:max-w-[400px] md:max-w-[440px] lg:max-w-[480px] aspect-[382/462] mx-auto"
    >
      <AnimatePresence>
        {/* Render cards in reverse order so top card is rendered last (on top) */}
        {visibleCards
          .map((card, reversedIndex) => {

            const actualIndex = visibleCards.length - 1 - reversedIndex;
            const isTopCard = actualIndex === 0;
            const cardStyle = getCardStyle(actualIndex);

            // If this card is exiting, use exit animation
            if (exitingCard && exitingCard.card.id === card.id) {
              return (
                <motion.div
                  key={card.id}
                  className="absolute w-full"
                  initial={{ x: 0, opacity: 1 }}
                  animate={{
                    x:
                      exitingCard.direction === 'right'
                        ? CARD_DECK_SWIPE.exitDistance
                        : -CARD_DECK_SWIPE.exitDistance,
                    opacity: 0,
                    rotate: exitingCard.direction === 'right' ? 15 : -15,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: CARD_DECK_SWIPE.exitDuration,
                    ease: 'easeOut',
                  }}
                  style={{
                    zIndex: 4, // Above all other cards during exit
                    originX: 0.5,
                    originY: 1, // Scale from bottom
                  }}
                >
                  <div>
                    <DeckCard card={card} />
                    <div />
                  </div>
                </motion.div>
              );
            }

            // Top card — conditionally draggable
            if (isTopCard && !exitingCard) {
              return (
                <motion.div
                  key={card.id}
                  className={`absolute w-full ${swipeEnabled ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  style={{
                    x: swipeEnabled ? x : 0,
                    opacity: swipeEnabled ? opacity : 1,
                    rotate: swipeEnabled ? rotate : 0,
                    scale: cardStyle.scale,
                    y: cardStyle.y,
                    zIndex: cardStyle.zIndex,
                    originX: 0.5,
                    originY: 1,
                  }}
                  animate={{
                    opacity: 1, // Ensure full opacity when at rest
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.2 } }}
                  onPointerDown={(e: React.PointerEvent) => {
                    pointerStart.current = { x: e.clientX, y: e.clientY };
                  }}
                  onClickCapture={(e: React.MouseEvent) => {
                    const dx = e.clientX - pointerStart.current.x;
                    const dy = e.clientY - pointerStart.current.y;
                    if (dx * dx + dy * dy > 25) {
                      e.stopPropagation();
                    }
                  }}
                  {...(swipeEnabled
                    ? {
                        drag: 'x' as const,
                        dragConstraints: { left: 0, right: 0 },
                        dragElastic: 1,
                        onDragEnd: handleDragEnd,
                        whileDrag: { cursor: 'grabbing' },
                      }
                    : {})}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 20,
                  }}
                >
                  <div>
                    <DeckCard card={card} />
                    <div />
                  </div>
                </motion.div>
              );
            }

            // Background cards - animated when promoted with spring
            // When a card is newly visible (entered from position 4+), it animates in
            // When a card is promoted (middle→top or back→middle), it scales up
            return (
              <motion.div
                key={card.id}
                className="absolute w-full pointer-events-none"
                initial={{
                  scale: CARD_DECK_DIMENSIONS.backCard.scale * 0.9, // Start slightly smaller
                  y: CARD_DECK_DIMENSIONS.backCard.offsetY - 10,    // Start slightly higher (more negative)
                  opacity: 0.5,                                     // Start semi-transparent
                }}
                animate={{
                  scale: cardStyle.scale,
                  y: cardStyle.y,
                  opacity: 1,
                }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                transition={{
                  type: 'spring',
                  stiffness: 260,
                  damping: 20,
                }}
                style={{
                  zIndex: cardStyle.zIndex,
                  originX: 0.5,
                  originY: 0,
                }}
              >
                {/* Overlay tint for back cards */}
                <div className="relative">
                  <DeckCard card={card} />
                  {cardStyle.overlay !== 'transparent' && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundColor: cardStyle.overlay,
                        borderRadius: '24px',
                      }}
                    />
                  )}
                </div>
              </motion.div>
            );
          })}
      </AnimatePresence>
    </div>
  );
}
